import { APP_MICROSOFT_SSO } from "./app";
import Http from "./http";
import ToggleService from "./toggle";
import { getCurrentUser } from "./api/user";
import { Settings, User } from "../redux/actions";

export default class LoginService {
  static async doLogin(email, password) {
    // TODO: remove this
    const params = new URLSearchParams();
    params.append("username", email.toLowerCase());
    params.append("password", password);
    params.append("client_id", process.env.REACT_APP_CLIENT_ID);
    params.append("client_secret", process.env.REACT_APP_CLIENT_SECRET);
    params.append("grant_type", "password");
    const url = `/v2/users/token`;
    try {
      const response = await Http.sharedInstance.getClient().post(url, params, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });
      const json = response.data;
      if (json) {
        return json;
      }
    } catch (e) {
      throw e;
    }
  }

  static async msSsoLogin(params) {
    const url = `/v2/auth/ms-login`;
    try {
      const response = await Http.sharedInstance.getClient().post(url, params);
      const json = response.data;
      if (json) {
        return json;
      }
    } catch (e) {
      throw e;
    }
  }

  static async getSignInLink() {
    const url = `/v2/auth/ms-signin`;
    try {
      const response = await Http.sharedInstance.getClient().get(url);
      const link = response.data;
      if (link) {
        return link;
      }
    } catch (e) {
      throw e;
    }
  }

  static async ssoGoogle({ email, token }) {
    const params = new URLSearchParams();
    params.append("username", email.toLowerCase());
    params.append("client_id", process.env.REACT_APP_CLIENT_ID);
    params.append("client_secret", process.env.REACT_APP_CLIENT_SECRET);
    params.append("grant_type", "password");
    // TODO: no such endpoint
    const url = `/v2/users/google`;
    try {
      const response = await Http.sharedInstance.getClient().post(url, params, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });
      const json = response.data;
      if (json) {
        return json;
      }
    } catch (e) {
      throw e;
    }
  }

  static async doImpersonate(email) {
    const params = new URLSearchParams();
    params.append("username", email.toLowerCase());
    params.append("client_id", process.env.REACT_APP_CLIENT_ID);
    params.append("client_secret", process.env.REACT_APP_CLIENT_SECRET);
    params.append("grant_type", "password");
    const url = `/v2/users/impersonate`;
    try {
      const response = await Http.sharedInstance.getClient().post(url, params, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });
      const json = response.data;
      if (json) {
        return json;
      }
    } catch (e) {
      throw e;
    }
  }

  /**
   * SAM-297 — opening an impersonated session, for every entry point.
   *
   * There were two hand-rolled copies of this (the header menu and the Users
   * screen) and they disagreed on the one step that matters. The Users screen
   * — the only one reachable on a tenant host, since the header entry is gated
   * on `settings.host === null` — dispatched `setProfileData`, i.e.
   * `SET_PROFILE`, which *merges*. `/v2/users/me` answers with `_id` and no
   * `id`, so `profile.id` survived the merge as the admin's id while the
   * access token became the subject's. The app then spent the whole session
   * asking about the wrong person with someone else's credentials:
   * `PUT /v2/users/<admin id>` → 403, every `asset/auth/download?fileAuth=
   * <admin id>.<sig>` → 400 (so every avatar on the page broken), the admin's
   * notification feed, the admin's filters. Salmon-tinted, but not actually
   * impersonating — which is what QA reported three times.
   *
   * Throws on every refusal. A silent `return false` is why this took three
   * rounds to pin down; callers surface the message.
   */
  static async startImpersonation(email, { settings, profile, accessToken, dispatch }) {
    if (!email) {
      throw new Error("impersonation: no target user");
    }

    if (!ToggleService._isToggleActive("impersonate", settings && settings.toggles)) {
      throw new Error("impersonation: the `impersonate` toggle is off for this tenant");
    }

    // Nothing is written down until the token is in hand: a failed attempt
    // must not leave `oldAccessToken` pointing at a session that never
    // changed, or "return to account" would log the admin out of their own.
    const token = await LoginService.doImpersonate(email);

    if (!token || !token.accessToken) {
      throw new Error("impersonation: the server returned no access token");
    }

    localStorage.setItem("oldUserProfile", JSON.stringify(profile));
    localStorage.setItem("oldAccessToken", JSON.stringify(accessToken));
    dispatch(User.setAccessToken(token));

    const me = await getCurrentUser();

    if (!me || !me._id) {
      localStorage.removeItem("oldUserProfile");
      localStorage.removeItem("oldAccessToken");
      dispatch(User.setAccessToken(accessToken));
      throw new Error("impersonation: could not load the impersonated profile");
    }

    // `/v2/users/me` has no `id`; half the app reads `profile.id`. Same
    // normalisation header.js already does after a plain login.
    me.id = me.id || me._id;
    // REPLACE. See the comment above for what merging costs.
    dispatch(User.setImpersonateProfileData(me));
    dispatch(Settings.loadAppSettings());

    window.location.href = "/";
  }

  static isImpersonating(settings) {
    if (!ToggleService._isToggleActive("impersonate", settings.toggles)) {
      return false;
    }

    return (
      localStorage.getItem("oldUserProfile") !== null &&
      localStorage.getItem("oldAccessToken") !== null
    );
  }

  static getSsoSettings = (settings) => {
    if (!settings || !settings.toggles) {
      return null;
    }

    const targetApp = settings.toggles.find(
      (a) => a.toggleId.toString() === APP_MICROSOFT_SSO
    );

    if (!targetApp) {
      return null;
    }

    if (!targetApp.isActive) {
      return null;
    }

    return targetApp.settings;
  };
}
